// main-pwa.jsx — entry point for the standalone PWA build.
//
// Unlike main.jsx (panel_custom — HA's frontend injects `hass`), the
// PWA owns the page top-to-bottom: auth, WS connection, state stream,
// and React mount. Once `useHaConnection()` reaches READY, the app
// mounts the same <App> the panel_custom build mounts; downstream
// components don't know they're in a PWA.
//
// HTML entry is panel/index-pwa.html → /src/main-pwa.jsx (Vite picks
// it up from rollupOptions.input in vite.config.pwa.js).

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import App from './app.jsx';
import { useHaConnection, STATUS } from './pwa/use-ha-connection.jsx';
import { ReconnectingPill, ServiceErrorToast } from './pwa/status-ui.jsx';
import { registerServiceWorker } from './pwa/register-sw.js';
import { LIGHT, FONT } from './theme.js';

// Icons + manifest live in index-pwa.html as static <link> tags
// (PWA mode, relative paths). No JS injection needed here — that
// pattern was only required for panel_custom where HA owns the
// served HTML.

function Root() {
  const { status, hass, error, connState, serviceError, dismissServiceError } =
    useHaConnection();

  if (status === STATUS.READY && hass) {
    return (
      <>
        <App hass={hass} narrow={false} />
        <ReconnectingPill connState={connState} />
        <ServiceErrorToast serviceError={serviceError} onDismiss={dismissServiceError} />
      </>
    );
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: LIGHT.paper,
      fontFamily: FONT.serif,
      color: LIGHT.ink,
      gap: 16, padding: 24, textAlign: 'center',
    }}>
      {status === STATUS.ERROR ? (
        <>
          <div style={{ fontSize: 36, fontWeight: 500 }}>Can't connect</div>
          <div style={{ fontFamily: FONT.sans,
            fontSize: 13, color: LIGHT.mute, maxWidth: 480 }}>
            {error?.message || 'Unknown error contacting Home Assistant.'}
          </div>
          <button onClick={() => window.location.reload()} style={{
            marginTop: 8, padding: '0 26px',
            border: 'none', background: LIGHT.primary, color: LIGHT.primaryInk,
            height: 48, borderRadius: 14, cursor: 'pointer',
            fontFamily: FONT.sans,
            fontSize: 15, fontWeight: 500,
          }}>Retry</button>
        </>
      ) : (
        <div style={{ fontSize: 30, fontWeight: 500, color: LIGHT.inkSoft }}>
          {status === STATUS.AUTHENTICATING ? 'Signing in…' : 'Connecting…'}
        </div>
      )}
    </div>
  );
}

const mount = document.getElementById('app');
createRoot(mount).render(<Root />);

registerServiceWorker();
